import { useEffect, useState } from "react";
import { Menu, X, Instagram } from "lucide-react";
import { SITE } from "@/lib/site-data";

const TikTokIcon = ({ size = 14 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.9a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-1.33Z" />
  </svg>
);

const NAV = [
  { label: "Home", href: "#home" },
  { label: "Services", href: "#services" },
  { label: "About", href: "#about" },
  { label: "Reviews", href: "#results" },
  { label: "Book Now", href: "#book" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState("home");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = ["home", "services", "about", "results", "book"];
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) io.observe(el);
    });
    return () => io.disconnect();
  }, []);

  const handleClick = (e, href) => {
    e.preventDefault();
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
        scrolled
          ? "bg-[#050505]/92 backdrop-blur-md border-b border-[rgba(215,200,175,0.14)]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 flex items-center justify-between h-16 lg:h-20 relative">
        {/* Logo */}
        <a
          href="#home"
          onClick={(e) => handleClick(e, "#home")}
          data-testid="site-logo"
          className="shrink-0 relative flex items-center"
          style={{ width: "170px", height: "100%" }}
          aria-label="Skin Refinery by Lacretia — Home"
        >
          <img
            src="/images/logo.png"
            alt="Skin Refinery by Lacretia"
            className="absolute left-0 top-1/2 -translate-y-1/2 h-28 md:h-36 lg:h-40 w-auto object-contain pointer-events-none"
          />
        </a>

        {/* Center nav */}
        <nav
          className="hidden lg:flex items-center gap-10"
          aria-label="Primary"
        >
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => handleClick(e, n.href)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`nav-link ${active === n.href.replace("#", "") ? "active" : ""}`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        {/* Right: socials + CTA */}
        <div className="hidden lg:flex items-center gap-5 shrink-0">
          <a
            href={SITE.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram — Skin Refinery"
            data-testid="header-instagram"
            className="text-[color:var(--sr-ivory)] hover:text-[color:var(--sr-gold)] transition-colors"
          >
            <Instagram size={16} strokeWidth={1.5} />
          </a>
          <a
            href={SITE.tiktok}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="TikTok — Skin Refinery"
            data-testid="header-tiktok"
            className="text-[color:var(--sr-ivory)] hover:text-[color:var(--sr-gold)] transition-colors"
          >
            <TikTokIcon size={16} />
          </a>
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-book-now-btn"
            className="btn-gold ml-2"
          >
            Book Now
          </a>
        </div>

        {/* Mobile menu button */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen((v) => !v)}
          className="lg:hidden text-[color:var(--sr-ivory)] p-2"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="mobile-menu"
        className={`lg:hidden overflow-hidden transition-[max-height,opacity] duration-700 ease-out ${
          mobileOpen ? "max-h-[600px] opacity-100" : "max-h-0 opacity-0"
        } bg-[#050505] border-t border-[rgba(215,200,175,0.14)]`}
      >
        <div className="px-8 py-8 flex flex-col gap-6">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={(e) => handleClick(e, n.href)}
              className="nav-link"
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {n.label}
            </a>
          ))}
          <div className="flex items-center gap-5 pt-2">
            <a
              href={SITE.instagram}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="text-[color:var(--sr-ivory)] hover:text-[color:var(--sr-gold)]"
            >
              <Instagram size={18} strokeWidth={1.5} />
            </a>
            <a
              href={SITE.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              className="text-[color:var(--sr-ivory)] hover:text-[color:var(--sr-gold)]"
            >
              <TikTokIcon size={18} />
            </a>
          </div>
          <a
            href={SITE.bookingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold self-start mt-2"
            data-testid="mobile-book-now-btn"
          >
            Book Now
          </a>
        </div>
      </div>
    </header>
  );
}
